import { useCallback, useEffect, useState } from "react";
import { api } from "../api";
import type { LogSource, PanelUser } from "../types";

export function LogsPage({
  accounts,
  currentUser,
  onRefreshAccounts
}: {
  accounts: PanelUser[];
  currentUser: PanelUser;
  onRefreshAccounts: () => Promise<void>;
}) {
  const staff =
    currentUser.role === "superadmin" || currentUser.role === "admin";
  const provisioned = accounts.filter(
    (a) => a.role === "user" && a.provisionStatus === "ready"
  );
  const [userId, setUserId] = useState(
    staff
      ? provisioned[0]?.id || ""
      : currentUser.provisionStatus === "ready"
        ? currentUser.id
        : ""
  );
  const [sources, setSources] = useState<LogSource[]>([]);
  const [selected, setSelected] = useState("");
  const [lines, setLines] = useState<string[]>([]);
  const [filter, setFilter] = useState("");
  const [lineCount, setLineCount] = useState(100);
  const [error, setError] = useState("");
  const [path, setPath] = useState("");

  const loadSources = useCallback(async () => {
    if (!userId) return;
    setError("");
    try {
      const res = await api<{ items: LogSource[] }>(
        `/api/v1/logs?userId=${encodeURIComponent(userId)}`
      );
      setSources(res.items || []);
      if (res.items?.[0] && !selected) setSelected(res.items[0].id);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }, [userId, selected]);

  const readLog = useCallback(async () => {
    if (!selected && !userId) return;
    setError("");
    try {
      const res = await api<{
        result: { lines: string[]; path: string; truncated: boolean };
      }>("/api/v1/logs/read", {
        method: "POST",
        body: JSON.stringify({
          userId,
          sourceId: selected,
          lines: lineCount,
          filter: filter || undefined
        })
      });
      setLines(res.result?.lines || []);
      setPath(res.result?.path || "");
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }, [userId, selected, lineCount, filter]);

  useEffect(() => {
    void loadSources();
  }, [loadSources]);

  useEffect(() => {
    if (selected) void readLog();
  }, [selected, readLog]);

  useEffect(() => {
    if (!userId && provisioned[0]?.id) setUserId(provisioned[0].id);
  }, [provisioned, userId]);

  if (!userId) {
    return (
      <>
        <div className="topbar">
          <div>
            <h1>Logs</h1>
            <p>Provision an end-user account first.</p>
          </div>
          <button
            type="button"
            className="btn btn-ghost"
            onClick={() => void onRefreshAccounts()}
          >
            Refresh
          </button>
        </div>
      </>
    );
  }

  return (
    <>
      <div className="topbar">
        <div>
          <h1>Logs</h1>
          <p>
            Permission-scoped · account home logs only for end-users · staff see
            system paths when present
          </p>
        </div>
        <div className="topbar-actions">
          {staff ? (
            <select
              className="inline-select"
              value={userId}
              onChange={(e) => {
                setUserId(e.target.value);
                setSelected("");
              }}
            >
              {provisioned.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.username}
                </option>
              ))}
            </select>
          ) : null}
          <button
            type="button"
            className="btn btn-ghost"
            onClick={() => void readLog()}
          >
            Refresh
          </button>
        </div>
      </div>

      {error ? <div className="error-banner">{error}</div> : null}

      <div className="fm-layout">
        <div className="fm-sidebar" style={{ minWidth: 240 }}>
          <div className="panel-form-title" style={{ marginBottom: 8 }}>
            Sources
          </div>
          {sources.length === 0 ? (
            <p className="faint">No log files yet</p>
          ) : (
            sources.map((s) => (
              <button
                key={s.id}
                type="button"
                className={`nav-item ${selected === s.id ? "active" : ""}`}
                style={{ width: "100%", textAlign: "left", marginBottom: 4 }}
                onClick={() => setSelected(s.id)}
              >
                <span className="nav-dot" />
                <span>
                  <div>{s.label}</div>
                  <div className="faint mono" style={{ fontSize: 11 }}>
                    {s.kind} ·{" "}
                    {s.sizeBytes < 1024
                      ? `${s.sizeBytes} B`
                      : `${(s.sizeBytes / 1024).toFixed(1)} KB`}
                  </div>
                </span>
              </button>
            ))
          )}
        </div>

        <div className="fm-main">
          <div className="topbar-actions" style={{ marginBottom: 12 }}>
            <input
              className="inline-select"
              style={{ minWidth: 160 }}
              placeholder="Filter…"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") void readLog();
              }}
            />
            <select
              className="inline-select"
              value={lineCount}
              onChange={(e) => setLineCount(Number(e.target.value))}
            >
              {[50, 100, 200, 500].map((n) => (
                <option key={n} value={n}>
                  {n} lines
                </option>
              ))}
            </select>
            <button
              type="button"
              className="btn btn-primary btn-inline"
              onClick={() => void readLog()}
            >
              Apply
            </button>
          </div>
          {path ? (
            <p className="card-meta mono faint" style={{ marginBottom: 8 }}>
              {path}
            </p>
          ) : null}
          <pre
            className="mono"
            style={{
              background: "rgba(0,0,0,0.35)",
              border: "1px solid var(--line)",
              borderRadius: 12,
              padding: 16,
              maxHeight: "60vh",
              overflow: "auto",
              fontSize: 12,
              lineHeight: 1.45,
              whiteSpace: "pre-wrap",
              wordBreak: "break-all"
            }}
          >
            {lines.length ? lines.join("\n") : "No lines"}
          </pre>
        </div>
      </div>
    </>
  );
}
